import { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { generalEnv } from '@nx-frontend/shared/env';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { BillingAddon } from '@nx-frontend/shared/types/paywall-types';
import { formatPrice } from '@nx-frontend/shared/utils';
import { getPaywallActionDiscounts } from './action-discounts';
import { trackUpgradePupopConfirm } from './analytics';
import { PaywallContext } from './paywall-context';
import { PaywallOrderSummaryCoupon } from './paywall-order-summary-coupon';
import { $paywallStore, applyCouponEvent, confirmSubscriptionEvent, setPaywallStateEvent } from './store';

export const PaywallOrderSummary = () => {
  const [paywallState, unleashFlags] = useUnit([$paywallStore, $unleashFlags]);

  const [applyCoupon, confirmSubscription, setPaywallState] = useUnit([
    applyCouponEvent,
    confirmSubscriptionEvent,
    setPaywallStateEvent,
  ]);

  const t = useTranslations();

  // This state is needed to show or hide the 'Today's charge' text only once, to avoid "flickering" when prorateDiff is calculated.
  const [prorateShown, setProrateShown] = useState(false);

  const { reason, getTimeDelta, mode } = useContext(PaywallContext);

  const hidePayWithLine = useMemo(() => {
    return mode === 'addons' && window.location.search.match(/trial_activated=1$/);
  }, [mode]);

  const isTrial = useMemo(() => {
    return (paywallState.orgBillingInfo?.trialEndsAt ?? 0) > Date.now() / 1000;
  }, [paywallState.orgBillingInfo]);

  const isEmptyState = useMemo(() => {
    const { selectedPlan, selectedAddons } = paywallState;
    const isEmpty = !selectedPlan && !Object.keys(selectedAddons).length;
    if (isEmpty) {
      if (paywallState.fetchingOrderSummary === false && paywallState.fetchingOrderSummary !== null) {
        setPaywallState({
          fetchingOrderSummary: null,
          billedToday: 0,
          prorateDiff: 0,
          credits: 0,
          discount: 0,
        });
      }
      setProrateShown(false);
    } else {
      if (Object.keys(paywallState.selectedAddons).length && !paywallState.selectedPlan && !paywallState.prorateDiff) {
        setProrateShown(false);
      }
    }
    return isEmpty;
  }, [paywallState, setPaywallState]);

  const onConfirm = useCallback(() => {
    confirmSubscription();
    const { selectedPlan, currentPlan } = paywallState;
    const planToTrack = selectedPlan ? selectedPlan : currentPlan ? currentPlan.code : undefined;
    if (planToTrack) {
      trackUpgradePupopConfirm(planToTrack, reason, getTimeDelta());
    }
  }, [confirmSubscription, getTimeDelta, paywallState, reason]);

  const { groupsToShow, totalWithForeverCouponsDiscount } = useMemo(() => {
    const groups: PaywallOrderSummaryGroupProps[] = [];
    /**
     * Total is sum for all items in order summary and addons from old plan
     */
    let total = 0;

    const plan = paywallState.plans.find((plan) => plan.code === paywallState.selectedPlan);

    const showPlanItem =
      paywallState.selectedPlan !== null && paywallState.selectedPlan !== paywallState.currentPlanCode;
    if (plan && showPlanItem) {
      const members =
        paywallState.onlyMembersCnt > paywallState.members ? paywallState.onlyMembersCnt : paywallState.members;
      const extraMembers = members - (plan?.minMembers || 0);
      const group = {
        title: t('features.paywall.plan_name_plan', { planName: plan.title }),
        items: [
          {
            title: t('features.paywall.plan_price_per_month', { price: plan?.planPrice.toFixed(0) }),
            price: plan?.exactPlanPrice || 0,
          },
          ...(extraMembers > 0
            ? [
                {
                  title: t('features.paywall.extra_members_price', {
                    extraMembers,
                    extraMemberPrice: plan?.extraMemberPrice,
                  }),
                  price: (plan?.exactExtraMemberPrice || 0) * extraMembers,
                },
              ]
            : []),
        ],
      };
      groups.push(group);
      total += group.items.reduce((acc: number, item: { price: number }) => acc + item.price, 0);
    }
    if (paywallState.currentPlanCode && !showPlanItem) {
      total += paywallState.currentSubscriptionTotal;
    }

    // Add addons to bill to total in case if select new plan, and current plan has addons
    let addonsToBillSum = 0;
    if (paywallState.currentAddonsToBill && showPlanItem) {
      addonsToBillSum += paywallState.currentAddonsToBill.reduce(
        (acc: number, item: BillingAddon) => acc + (item.price || 0) * (item.quantity || 0),
        0
      );
    }
    total += addonsToBillSum;

    Object.values(paywallState.selectedAddons).forEach((item) => {
      const addon = paywallState.addons.find((a) => a.code === item.code);
      const price = addon?.price || 0;
      const pricePerMonth = addon?.pricePerMonth || 0;
      const group = {
        title: (addon?.titleLocKey && t(addon.titleLocKey)) ?? addon?.title,
        items: [
          {
            title: t('features.paywall.item_price_per_month', {
              itemQuantity: item.quantity,
              pricePerMonth,
            }),
            price: price * (item.quantity || 0),
          },
        ],
      };
      groups.push(group);
      const sum = group.items.reduce((acc: number, item: { price: number }) => acc + item.price, 0);
      total += sum;
    });

    if (paywallState.prorateDiff && !isTrial) {
      !prorateShown && setProrateShown(true);
      groups.push({
        id: 'prorate',
        title: t('features.paywall.prorate'),
        tooltip: (
          <div className="max-w-[240px] text-xs font-normal p-1">{t('features.paywall.proration_explanation')}</div>
        ),
        items: [
          {
            title: <>{t('features.paywall.prorate_item_title', { planName: paywallState.currentPlan?.name.en })}</>,
            price: -paywallState.prorateDiff,
          },
        ],
      });
    }

    if (paywallState.credits) {
      groups.push({
        id: 'credits',
        title: t('features.paywall.credits'),
        items: [{ title: t('features.paywall.used_credits'), price: -paywallState.credits }],
      });
    }

    if (paywallState.discount) {
      const action = unleashFlags[UnleashFeatures.SHOW_DISCOUNT_ACTION] ? getPaywallActionDiscounts() : null;
      const coupons = paywallState.couponRedemption;

      const isAction = action && coupons.length === 1 && coupons[0].coupon.code === action.coupon;

      const groupTitle = isAction && action.orderSummaryGroupTitle ? action.orderSummaryGroupTitle : null;
      const itemsTitle =
        isAction && action.orderSummaryItemTitle
          ? action.orderSummaryItemTitle
          : t('features.paywall.promo_code_discount');

      groups.push({
        id: 'discount',
        title: groupTitle,
        items: [{ title: itemsTitle, price: -paywallState.discount }],
      });
    }

    const { foreverCouponsDiscount } = paywallState;
    let totalWithForeverCouponsDiscount = total;
    if (foreverCouponsDiscount) {
      totalWithForeverCouponsDiscount = +(total - total * (foreverCouponsDiscount / 100)).toFixed(2);
    }

    return { groupsToShow: groups, total, totalWithForeverCouponsDiscount };
  }, [paywallState, prorateShown, unleashFlags, setProrateShown, isTrial, t]);

  const currentPeriodEndsAtStr = useMemo(() => {
    const date = new Date(paywallState.currentPeriodEndsAt);
    return `${date.toLocaleString('en', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
  }, [paywallState.currentPeriodEndsAt]);

  const billedToday = useMemo(() => {
    if (paywallState.fetchingOrderSummary === true && !isEmptyState) {
      return (
        <div className="min-w-[50px] h-[24px]">
          <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e1e1e1">
            <Skeleton containerClassName="w-full" className="h-[24px]" />
          </SkeletonTheme>
        </div>
      );
    }
    return <>{formatPrice(isEmptyState ? 0 : paywallState.billedToday || 0)}</>;
  }, [isEmptyState, paywallState.fetchingOrderSummary, paywallState.billedToday]);

  const nextPaymentDate = useMemo(() => {
    return dayjs(paywallState.currentPeriodEndsAt).format('MMMM D, YYYY');
  }, [paywallState.currentPeriodEndsAt]);

  const termsOfUseUrl = `https://${generalEnv.nimbusDomain}/terms-and-conditions/`;
  const privacyPolicyUrl = `https://${generalEnv.nimbusDomain}/privacy/`;

  return (
    <div
      className={classNames(
        'paywall__order-summary border border-l-neutral-200 w-[378px] rounded-r-xl px-6 py-11 flex flex-col',
        {
          'bg-gray-100': mode !== 'addons',
          'bg-white': mode === 'addons',
        }
      )}
    >
      <div
        className={classNames('text-2xl font-semibold mb-6', {
          'text-center': mode !== 'addons',
        })}
      >
        {t('features.paywall.order_summary')}
      </div>
      {!isEmptyState && prorateShown ? (
        <div className="rounded-[10px] bg-white border border-neutral-200 mb-4 text-sm text-neutral-800 p-4">
          Today's charge is prorated based on the remaining days in your current billing cycle, ending on{' '}
          {currentPeriodEndsAtStr}.
        </div>
      ) : null}

      <div
        className={classNames(
          'rounded-[10px] bg-white border border-neutral-200 mb-4 relative overflow-hidden duration-500'
        )}
      >
        {isEmptyState ? (
          <div className="text-sm text-neutral-500 p-5">{t('features.paywall.choose_your_base_plan')}</div>
        ) : (
          <>
            <div className="p-5">
              {groupsToShow.map((group, index) => (
                <PaywallOrderSummaryGroup
                  key={index}
                  id={group.id}
                  title={group.title}
                  items={group.items}
                  tooltip={group.tooltip}
                />
              ))}
              {!paywallState.discount && !paywallState.couponApplied ? (
                <PaywallOrderSummaryCoupon onApply={applyCoupon} error={paywallState.couponError} />
              ) : null}
            </div>{' '}
          </>
        )}
        <div className="border-t border-t-neutral-200 px-5 py-4 flex text-neutral-800 text-base font-bold">
          <div className="flex-1">{t('features.paywall.billed_today')}</div>
          <div>{billedToday}</div>
        </div>
      </div>

      {!isEmptyState && !hidePayWithLine ? (
        <div className="text-xs text-center text-neutral-800 mb-2">
          {paywallState.cardLastFour ? (
            <>
              {t.rich('features.paywall.pay_with_card_with_last_four', {
                cardType: paywallState.cardType,
                lastFour: paywallState.cardLastFour,
                strong: (chunk) => <span className="font-bold">{chunk}</span>,
              })}
            </>
          ) : (
            <>
              {t('features.paywall.pay_with_card_without_last_four', {
                cardType: paywallState.cardType,
              })}
            </>
          )}{' '}
          <a
            href={`${paywallState.settings.userBillingUrl}/edit-billinginfo`}
            className="ml-1 text-neutral-500 cursor-pointer font-bold underline"
            target="_blank"
            rel="noreferrer"
          >
            {t('features.paywall.change')}
          </a>
        </div>
      ) : null}

      <Button
        type={ButtonType.FLAT}
        size={ButtonSize.MD}
        onClick={onConfirm}
        themeColors={webnotesThemeColors}
        themeStyle={webnotesThemeStyle}
        className="w-full h-[36px]"
        disabled={
          isEmptyState ||
          paywallState.fetchingConfirmSubscription ||
          !!paywallState.fetchingOrderSummary ||
          !!paywallState.previewError
        }
        disabledClassName={isEmptyState ? 'bg-black bg-opacity-5 text-black text-opacity-70' : ''}
      >
        {t('features.paywall.confirm')}
      </Button>

      {!isEmptyState ? (
        <div className="text-xs text-center text-neutral-500 mt-2">
          Your next payment will be <span className="font-bold">${totalWithForeverCouponsDiscount}</span> on{' '}
          {nextPaymentDate} <span>{paywallState.billingPeriod}</span> until you cancel your subscription.
        </div>
      ) : null}
      <div className="text-xs text-center text-neutral-500 pt-2 mt-auto">
        {t.rich('features.paywall.terms_and_policies', {
          terms: (chunks) => (
            <a
              href={termsOfUseUrl}
              className="text-cyan-900 hover:text-cyan-700 underline"
              target="_blank"
              rel="noreferrer"
            >
              {chunks}
            </a>
          ),
          policy: (chunks) => (
            <a
              href={privacyPolicyUrl}
              className="text-cyan-900 hover:text-cyan-700 underline"
              target="_blank"
              rel="noreferrer"
            >
              {chunks}
            </a>
          ),
        })}
      </div>
    </div>
  );
};

interface PaywallOrderSummaryGroupItem {
  price: number;
  id?: string;
  title?: ReactElement | string;
}

type PaywallOrderSummaryGroupItemProps = PaywallOrderSummaryGroupItem & { showPrice?: boolean };

interface PaywallOrderSummaryGroupProps {
  items: PaywallOrderSummaryGroupItem[];
  id?: string;
  title?: ReactElement | string | null;
  tooltip?: ReactElement | string;
}

const PaywallOrderSummaryGroup = (props: PaywallOrderSummaryGroupProps) => {
  const { id, title, items, tooltip } = props;
  const groupTotal = items.map((item) => item.price).reduce((acc, item) => acc + item);
  const [paywallState] = useUnit([$paywallStore]);
  return (
    <div className="mb-4">
      {title ? (
        <div className="text-neutral-800 text-base flex items-center justify-between">
          <div className="font-bold flex items-center gap-2">
            {title}{' '}
            {tooltip ? (
              <TippyTooltip message={tooltip} position="top-start">
                <span className="text-neutral-500 text-sm cursor-pointer hover:bg-neutral-100 rounded-sm flex items-center justify-center duration-200">
                  <Icon icon={IconsMap.INFO} className="w-[16px] h-[16px] text-black text-opacity-55" />
                </span>
              </TippyTooltip>
            ) : null}
          </div>
          {paywallState.fetchingOrderSummary && ['prorate', 'credits', 'discount'].includes(id ?? '') ? (
            <div className="min-w-[50px] text-base h-[24px] flex items-center">
              <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e1e1e1">
                <Skeleton containerClassName="w-full" className="h-[24px]" />
              </SkeletonTheme>
            </div>
          ) : (
            <div>{formatPrice(groupTotal)}</div>
          )}
        </div>
      ) : null}

      {items.map((item, index) => (
        <PaywallOrderSummaryGroupItem
          key={index}
          id={id}
          title={item.title}
          price={item.price}
          showPrice={title ? false : true}
        />
      ))}
    </div>
  );
};

const PaywallOrderSummaryGroupItem = (props: PaywallOrderSummaryGroupItemProps) => {
  const [paywallState] = useUnit([$paywallStore]);
  const { id = '', title, price } = props;
  return (
    <div
      className={classNames('flex gap-1', {
        'items-end': id === 'prorate',
        'items-center': id !== 'prorate',
      })}
    >
      <div className="flex-1 text-neutral-500 text-sm">
        <div className={classNames('max-w-[210px]')}>{title}</div>
      </div>
      {props.showPrice &&
        (paywallState.fetchingOrderSummary && ['prorate', 'credits', 'discount'].includes(id) ? (
          <div className="min-w-[50px] text-base h-[24px] flex items-center">
            <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e1e1e1">
              <Skeleton containerClassName="w-full" className="h-[24px]" />
            </SkeletonTheme>
          </div>
        ) : (
          <div className="text-base">{formatPrice(price)}</div>
        ))}
    </div>
  );
};
